import React, { useMemo, useState } from "react";
import styled from "@emotion/styled";

import * as tokens from "../../design-tokens";
import { Typography } from "../Typography";
import { weekdays } from "../../helpers/weekdaysMonths";
import { useScreenSizes } from "../../hooks/useSizes";
import {
  formatDateToString,
  getAmountOfDays,
  getDaysFromMonthAndYear,
  populateMatrix,
  shouldDisablePastMonth,
} from "./helpers";
import { MonthHeader } from "./MonthHeader";
import { SingleDaySlot } from "./SingleDaySlot";

const CalendarTable = ({
  availableDates,
  onSelectedDate,
  selectedDate,
  activeMonth,
  activeYear,
  disabledPastDays,
}: CalendarContainerBaseProps) => {
  const matrix = useMemo(() => {
    return populateMatrix(activeMonth, activeYear);
  }, [activeMonth, activeYear]);

  return (
    <StyledGrid cols={weekdays.length}>
      {matrix.map((weekdays) =>
        weekdays.map((date) => (
          <SingleDaySlot
            key={formatDateToString(date)}
            onSelectedDate={onSelectedDate}
            selectedDate={selectedDate}
            singleDate={date}
            activeMonth={activeMonth}
            availableDates={availableDates}
            disabledPastDays={disabledPastDays}
          />
        ))
      )}
    </StyledGrid>
  );
};

const CalendarMobile = ({
  availableDates,
  onSelectedDate,
  selectedDate,
  activeMonth,
  activeYear,
  disabledPastDays,
}: CalendarContainerBaseProps) => {
  const monthDaysList = useMemo(() => {
    const amountOfDays = getAmountOfDays(activeMonth, activeYear);
    return getDaysFromMonthAndYear(activeMonth, activeYear, amountOfDays);
  }, [activeMonth, activeYear]);

  return (
    <StyledGrid cols={1}>
      {monthDaysList.map((day) => (
        <SingleDaySlot
          key={formatDateToString(day)}
          onSelectedDate={onSelectedDate}
          selectedDate={selectedDate}
          singleDate={day}
          activeMonth={activeMonth}
          availableDates={availableDates}
          disabledPastDays={disabledPastDays}
          isMobile
        />
      ))}
    </StyledGrid>
  );
};

const WeekHeader = () => {
  return (
    <StyledDateHeader>
      <StyledGrid cols={weekdays.length}>
        {weekdays.map((day) => {
          return (
            <Typography key={day} align="center">
              {day}
            </Typography>
          );
        })}
      </StyledGrid>
    </StyledDateHeader>
  );
};

const DateCalendar = ({
  selectedDate,
  availableDates,
  disabledFutureNavigation,
  disabledPastNavigation = true,
  disabledPastDays = true,
  onSelectedDate,
  onNextMonth,
  onPrevMonth,
}: DateSchedulerProps) => {
  const [formattedDate, setFormattedDate] = useState(
    new Date().toLocaleString()
  );

  const { mobile } = useScreenSizes();

  const disablePastMonth = useMemo(() => {
    if (!disabledPastNavigation) {
      return false;
    }
    return shouldDisablePastMonth(formattedDate);
  }, [formattedDate, disabledPastNavigation]);

  const onPastMonth = () => {
    const activeDate = new Date(formattedDate);
    activeDate.setMonth(activeDate.getMonth() - 1);
    const formattedNewDate = formatDateToString(activeDate);
    setFormattedDate(formattedNewDate);
    onPrevMonth?.(formattedNewDate);
  };

  const onFutureMonth = () => {
    const activeDate = new Date(formattedDate);
    activeDate.setMonth(activeDate.getMonth() + 1);
    const formattedNewDate = formatDateToString(activeDate);
    setFormattedDate(formattedNewDate);
    onNextMonth?.(formattedNewDate);
  };

  const activeDate = new Date(formattedDate);
  const activeMonth = activeDate.getMonth();
  const activeYear = activeDate.getFullYear();

  const availableDatesISO = availableDates?.map((dateString) =>
    formatDateToString(new Date(dateString))
  );

  return (
    <>
      <MonthHeader
        dateString={formattedDate}
        onNext={onFutureMonth}
        onPrevious={onPastMonth}
        disabledFutureNavigation={disabledFutureNavigation}
        disabledPastNavigation={disablePastMonth}
      />
      {!mobile && <WeekHeader />}
      {mobile ? (
        <CalendarMobile
          onSelectedDate={onSelectedDate}
          activeMonth={activeMonth}
          activeYear={activeYear}
          selectedDate={selectedDate}
          disabledPastDays={disabledPastDays}
          availableDates={availableDatesISO}
        />
      ) : (
        <CalendarTable
          onSelectedDate={onSelectedDate}
          activeMonth={activeMonth}
          activeYear={activeYear}
          selectedDate={selectedDate}
          disabledPastDays={disabledPastDays}
          availableDates={availableDatesISO}
        />
      )}
    </>
  );
};

interface CalendarContainerBaseProps {
  activeMonth: number;
  activeYear: number;
  selectedDate: string;
  disabledPastDays?: boolean;
  availableDates?: string[];
  onSelectedDate: (date: string) => void;
}

type DateSchedulerProps = {
  disabledPastNavigation?: boolean;
  disabledFutureNavigation?: boolean;
  disabledPastDays?: boolean;
  selectedDate?: string;
  availableDates?: string[];
  onSelectedDate: (date: string) => void;
  onNextMonth?: (date: string) => void;
  onPrevMonth?: (date: string) => void;
};

const StyledDateHeader = styled.div`
  display: grid;
  border-top: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  border-bottom: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  padding-top: 16px;
  padding-bottom: 16px;
  margin-bottom: 16px;
`;

const StyledGrid = styled.div<{ cols: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.cols}, minmax(0, 1fr));
  gap: 16px;
`;

export type { DateSchedulerProps };
export { DateCalendar };
